import { isSignedMagnitudeU128, isU64 } from './integer.js';
import type { OutcomePoolQuoteContext } from './outcome-pool.js';
import { buildPnlBreakdown, POSITION_PNL_VIEW_MODE, selectPnlView } from './pnl-breakdown.js';
import {
  checkedOutcomePoolQuoteContextFromSnapshot,
  type MarketPoolQuoteSnapshot,
} from './snapshot-context.js';
import type { PoolMathResult } from './types.js';
import { resolveQuoteVenue } from './venue.js';

export interface ExactTraderOutcome {
  readonly outcomeMint: string;
  readonly outcomeIndex: number;
  readonly balance: string;
  readonly claimRightsHoldingD6: string;
  readonly remainingCostBasisGrossD6: string;
  readonly realizedPnlGrossD6: string;
  readonly remainingCostBasisNetD6: string;
  readonly realizedPnlNetD6: string;
  readonly realizedPenaltyD6: string;
  readonly prizeClaimableD6: string;
}
export interface ExactTraderPosition {
  readonly realizedPnlGrossD6: string;
  readonly outcomes: readonly ExactTraderOutcome[];
}
export interface ExactTraderPnl {
  realizedPnlD6: bigint;
  unrealizedPnlD6: bigint;
  totalPnlD6: bigint;
}

/** Same fold as a position card: every outcome through `buildPnlBreakdown`, summed in the one view. */
export function exactTraderPnlD6(
  row: ExactTraderPosition,
  snapshot: MarketPoolQuoteSnapshot,
): PoolMathResult<ExactTraderPnl> {
  const rowRealizedPnlD6 = signedIntegerWire(row.realizedPnlGrossD6);
  if (rowRealizedPnlD6 === null || !isSignedMagnitudeU128(rowRealizedPnlD6)) {
    return { available: false, reason: 'invalid_amount' };
  }

  let realizedPnlD6 = BigInt(0);
  let unrealizedPnlD6 = BigInt(0);
  // Cross-check against the row total, which is always the gross sum: the
  // outcome fold below reports whichever view the clients render, so the
  // accounting check must not move with `POSITION_PNL_VIEW_MODE`.
  let realizedGrossD6 = BigInt(0);
  for (const outcome of row.outcomes) {
    const outcomeBalanceD6 = signedIntegerWire(outcome.balance);
    const claimRightsBalanceD6 = signedIntegerWire(outcome.claimRightsHoldingD6);
    const remainingCostBasisGrossD6 = signedIntegerWire(outcome.remainingCostBasisGrossD6);
    const remainingCostBasisNetD6 = signedIntegerWire(outcome.remainingCostBasisNetD6);
    const realizedPnlGrossD6 = signedIntegerWire(outcome.realizedPnlGrossD6);
    const realizedPnlNetD6 = signedIntegerWire(outcome.realizedPnlNetD6);
    const realizedPenaltyD6 = signedIntegerWire(outcome.realizedPenaltyD6);
    const prizeClaimableD6 = signedIntegerWire(outcome.prizeClaimableD6);
    if (
      outcomeBalanceD6 === null ||
      claimRightsBalanceD6 === null ||
      remainingCostBasisGrossD6 === null ||
      remainingCostBasisNetD6 === null ||
      realizedPnlGrossD6 === null ||
      realizedPnlNetD6 === null ||
      realizedPenaltyD6 === null ||
      prizeClaimableD6 === null
    ) {
      return { available: false, reason: 'invalid_amount' };
    }
    const context = checkedTraderOutcomeContext(snapshot, outcome);
    if (!context.available) return context;
    const breakdown = buildPnlBreakdown({
      context: context.value,
      outcomeBalanceD6,
      claimRightsBalanceD6,
      remainingCostBasisGrossD6,
      remainingCostBasisNetD6,
      realizedPnlGrossD6,
      realizedPnlNetD6,
      realizedPenaltyD6,
      prizeClaimableD6,
    });
    if (!breakdown.available) return breakdown;
    const view = selectPnlView(breakdown.value, POSITION_PNL_VIEW_MODE);
    realizedPnlD6 += view.realizedD6;
    unrealizedPnlD6 += view.unrealizedD6;
    realizedGrossD6 += breakdown.value.realizedGrossD6;
    if (!isSignedMagnitudeU128(realizedPnlD6) || !isSignedMagnitudeU128(unrealizedPnlD6)) {
      return { available: false, reason: 'invalid_amount' };
    }
  }

  if (realizedGrossD6 !== rowRealizedPnlD6) {
    return { available: false, reason: 'malformed_position_accounting' };
  }
  const totalPnlD6 = realizedPnlD6 + unrealizedPnlD6;
  if (!isSignedMagnitudeU128(totalPnlD6)) {
    return { available: false, reason: 'invalid_amount' };
  }
  return { available: true, value: { realizedPnlD6, unrealizedPnlD6, totalPnlD6 } };
}

// Snapshots are immutable; share checked pool context across positions valued against one snapshot.
type ContextCache = WeakMap<
  MarketPoolQuoteSnapshot,
  Map<string, PoolMathResult<OutcomePoolQuoteContext>>
>;
const contexts: ContextCache = new WeakMap();

function checkedTraderOutcomeContext(
  snapshot: MarketPoolQuoteSnapshot,
  outcome: ExactTraderOutcome,
): PoolMathResult<OutcomePoolQuoteContext> {
  let byOutcome = contexts.get(snapshot);
  if (byOutcome === undefined) {
    byOutcome = new Map();
    contexts.set(snapshot, byOutcome);
  }
  const key = `${outcome.outcomeIndex}:${outcome.outcomeMint}`;
  const existing = byOutcome.get(key);
  if (existing !== undefined) return existing;
  const result = positionQuoteContext(
    snapshot,
    snapshot.market,
    outcome.outcomeIndex,
    outcome.outcomeMint,
  );
  byOutcome.set(key, result);
  return result;
}

/**
 * Quote context for one held outcome of a snapshot. An outcome the snapshot
 * no longer carries is a settled loser and resolves to a terminal context, so
 * a P&L reader can still value it (at zero) instead of failing.
 */
export function positionQuoteContext(
  snapshot: MarketPoolQuoteSnapshot,
  market: string,
  outcomeIndex: number,
  outcomeMint: string,
  stateStale: boolean = false,
): PoolMathResult<OutcomePoolQuoteContext> {
  const checked = checkedOutcomePoolQuoteContextFromSnapshot(
    snapshot,
    market,
    outcomeIndex,
    outcomeMint,
    stateStale,
  );
  if (checked.available) return { available: true, value: checked.value.context };
  if (checked.reason !== 'snapshot_outcome_missing') return checked;

  const venue = {
    lifecycle: snapshot.lifecycle,
    outcomeIndex,
    winningOutcomeIndex: snapshot.winningOutcomeIndex,
    bondingCurveClosed: snapshot.bondingCurvesClosed === true,
    hasCpSwapPool: false,
  } as const;
  const resolvedVenue = resolveQuoteVenue(venue);
  if (resolvedVenue.available || resolvedVenue.reason !== 'terminal_losing_outcome') return checked;
  const slot = unsignedIntegerWire(snapshot.chainSlot);
  const unixTimestampSeconds = unsignedIntegerWire(snapshot.chainUnixTimestampSeconds);
  if (slot === null || unixTimestampSeconds === null) {
    return { available: false, reason: 'invalid_amount' };
  }
  return {
    available: true,
    value: {
      chainClock: { slot, unixTimestampSeconds },
      poolModel: 'missing',
      venue,
    },
  };
}

function signedIntegerWire(value: string): bigint | null {
  if (!/^-?(?:0|[1-9]\d*)$/.test(value)) return null;
  return BigInt(value);
}

function unsignedIntegerWire(value: string): bigint | null {
  const parsed = signedIntegerWire(value);
  return parsed !== null && isU64(parsed) ? parsed : null;
}
